import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Smartphone, Copy, Check, LogOut, Share2, Bookmark } from "lucide-react";

// Compact bookmarklet: reads JSON-LD → OpenGraph → DOM regex, then opens
// <RADAR_URL>/quick-add?url=&title=&price=&currency=&image=&source=
// The template string ${RADAR_URL} gets swapped in at runtime with the
// current origin so it works both in preview and after deploy.
const BOOKMARKLET = (radarUrl) =>
`javascript:void((function(){var d=document,q=(s,a='content')=>{var e=d.querySelector(s);return e?e.getAttribute(a):''},t=(s)=>{var e=d.querySelector(s);return e?e.textContent.trim():''},ld=null;for(var s of d.querySelectorAll("script[type='application/ld+json']")){try{var j=JSON.parse(s.textContent);if(Array.isArray(j))j=j[0];if(j&&j['@type']&&String(j['@type']).indexOf('Product')>-1){ld=j;break}}catch(e){}}var p=null,c='USD',i='';if(ld){var o=ld.offers;if(Array.isArray(o))o=o[0];if(o){p=parseFloat(o.price||o.lowPrice||0)||null;c=o.priceCurrency||'USD'}if(typeof ld.image==='string')i=ld.image;else if(Array.isArray(ld.image)&&ld.image[0])i=typeof ld.image[0]==='string'?ld.image[0]:ld.image[0].url}if(!p){var pp=q('meta[property="og:price:amount"]')||q('meta[property="product:price:amount"]');if(pp)p=parseFloat(pp);var cc=q('meta[property="og:price:currency"]')||q('meta[property="product:price:currency"]');if(cc)c=cc}if(!p){var pel=d.querySelector("[itemprop='price'],[class*='sale-price'],[class*='current-price'],[class*='product-price']");if(pel){var m=(pel.textContent||'').match(/([£$€])\\s?(\\d+[.,]?\\d*)/);if(m){p=parseFloat(m[2].replace(',','.'));c=m[1]==='£'?'GBP':m[1]==='€'?'EUR':'USD'}}}var tt=(ld&&ld.name)||q('meta[property="og:title"]')||t('h1')||d.title,img=i||q('meta[property="og:image"]')||q('meta[name="twitter:image"]')||'',u=new URL('${radarUrl}/quick-add');u.searchParams.set('url',location.href.split('#')[0]);u.searchParams.set('title',(tt||'').trim().slice(0,240));if(p)u.searchParams.set('price',p);u.searchParams.set('currency',c);if(img)u.searchParams.set('image',img);u.searchParams.set('source',location.hostname.replace(/^www\\./,''));window.open(u.toString(),'_blank')})())`;

export default function Mobile() {
    const { logout } = useAuth();
    const nav = useNavigate();
    const [copied, setCopied] = useState(false);
    const radarUrl = useMemo(() => window.location.origin, []);
    const bookmarklet = useMemo(() => BOOKMARKLET(radarUrl), [radarUrl]);

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(bookmarklet);
            setCopied(true);
            toast.success("Copied — now paste it as a bookmark URL");
            setTimeout(() => setCopied(false), 2500);
        } catch (e) {
            toast.error("Couldn't copy — long-press the button and copy manually");
        }
    };

    return (
        <div className="min-h-screen bg-neutral-50">
            <header className="border-b border-neutral-200 bg-white sticky top-0 z-30">
                <div className="max-w-3xl mx-auto px-5 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button onClick={() => nav("/wishlist")} className="border border-neutral-300 h-10 w-10 flex items-center justify-center hover:border-black" data-testid="back-to-wishlist">
                            <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
                        </button>
                        <div>
                            <div className="text-[10px] uppercase tracking-widest text-neutral-500">Setup</div>
                            <h1 className="font-display text-2xl tracking-tight leading-none mt-0.5">Phone</h1>
                        </div>
                    </div>
                    <button onClick={logout} className="border border-neutral-300 h-10 w-10 flex items-center justify-center hover:border-black" title="Sign out">
                        <LogOut className="h-4 w-4" strokeWidth={1.5} />
                    </button>
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-5 py-6">
                <div className="border border-neutral-200 bg-white p-5 mb-6">
                    <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 bg-yellow-300 border border-black flex items-center justify-center">
                            <Smartphone className="h-5 w-5" strokeWidth={1.5} />
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-widest text-neutral-500">On your phone</div>
                            <div className="font-display text-xl leading-none">One-tap add from any store</div>
                        </div>
                    </div>
                    <p className="font-body text-sm text-neutral-600 leading-relaxed">
                        Chrome extensions don't work on phones — but a <span className="font-semibold text-black">bookmarklet</span> gives you the same power. It's a bookmark that, when you tap it on a product page, reads the title + price + image and drops the item on your Radar. Works on iOS Safari + Android Chrome.
                    </p>
                </div>

                {/* The bookmarklet itself */}
                <div className="border-2 border-yellow-300 bg-white p-5 mb-6">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2 flex items-center gap-1">
                        <Bookmark className="h-3 w-3" strokeWidth={1.5} /> Your bookmarklet
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                        {/* On desktop this is draggable to the bookmarks bar */}
                        <a
                            href={bookmarklet}
                            onClick={(e) => e.preventDefault()}
                            className="bg-black text-white px-5 h-12 flex items-center gap-2 uppercase tracking-widest text-[11px] font-semibold cursor-grab active:cursor-grabbing hover:bg-neutral-800"
                            draggable="true"
                            data-testid="bookmarklet-btn"
                        >
                            <Share2 className="h-4 w-4" strokeWidth={1.5} /> ➕ Track on Radar
                        </a>
                        <button
                            onClick={copy}
                            className="border border-black px-4 h-12 flex items-center gap-2 uppercase tracking-widest text-[11px] font-semibold hover:bg-black hover:text-white"
                            data-testid="copy-bookmarklet-btn"
                        >
                            {copied ? <Check className="h-4 w-4" strokeWidth={2} /> : <Copy className="h-4 w-4" strokeWidth={1.5} />}
                            {copied ? "Copied" : "Copy code"}
                        </button>
                    </div>
                    <p className="font-body text-xs text-neutral-500 mt-3">
                        Desktop: drag the yellow button to your bookmarks bar. Phone: tap "Copy code" and follow the steps below.
                    </p>
                </div>

                {/* Install steps per platform */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    {/* iOS Safari */}
                    <div className="bg-white border border-neutral-200 p-5">
                        <div className="text-[10px] uppercase tracking-widest text-neutral-500">iOS · Safari</div>
                        <div className="font-display text-lg mt-1 mb-3">iPhone / iPad</div>
                        <ol className="text-sm font-body space-y-2 text-neutral-700 list-decimal ml-4">
                            <li>On this page in Safari, tap <span className="font-semibold">Share</span> → <span className="font-semibold">Add Bookmark</span> → Save (this creates a placeholder).</li>
                            <li>Tap the <span className="font-semibold">Bookmarks icon</span> at the bottom → <span className="font-semibold">Edit</span> → tap the bookmark you just created.</li>
                            <li>Change the <span className="font-semibold">name</span> to "Track on Radar".</li>
                            <li>Delete the URL, then <span className="font-semibold">paste the code you copied above</span> — starts with <span className="font-mono bg-neutral-100 px-1">javascript:</span>. Tap Done.</li>
                            <li>Go to any product page → tap Bookmarks → tap "Track on Radar". A new tab opens with the item pre-loaded — tap <span className="font-semibold">Track this price</span> ✓.</li>
                        </ol>
                    </div>

                    {/* Android Chrome */}
                    <div className="bg-white border border-neutral-200 p-5">
                        <div className="text-[10px] uppercase tracking-widest text-neutral-500">Android · Chrome</div>
                        <div className="font-display text-lg mt-1 mb-3">Android phone</div>
                        <ol className="text-sm font-body space-y-2 text-neutral-700 list-decimal ml-4">
                            <li>Tap the <span className="font-semibold">⋮ menu</span> in Chrome → tap the <span className="font-semibold">star</span> to bookmark this page.</li>
                            <li>Tap ⋮ → <span className="font-semibold">Bookmarks</span> → find the new bookmark → tap ⋮ next to it → <span className="font-semibold">Edit</span>.</li>
                            <li>Rename to "Track on Radar".</li>
                            <li>Replace the URL with the <span className="font-semibold">code you copied above</span>. Save.</li>
                            <li>To use it: type <span className="font-mono bg-neutral-100 px-1">Track on Radar</span> in the address bar on any product page → tap the bookmark suggestion.</li>
                        </ol>
                    </div>
                </div>

                {/* Fallback */}
                <div className="bg-white border border-neutral-200 p-5">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500">If bookmarklets feel fiddly</div>
                    <div className="font-display text-lg mt-1 mb-3">Just paste a URL — always works</div>
                    <p className="font-body text-sm text-neutral-600 leading-relaxed">
                        On any phone, open the wishlist tab, tap <span className="font-semibold">Add Item</span>, and paste the product URL. Our server reads the page and pre-fills everything — one tap to save. Slower than a bookmark, but zero setup.
                    </p>
                    <Link to="/wishlist" className="inline-block mt-3 border border-black px-4 h-10 uppercase tracking-widest text-[10px] font-semibold leading-[2.5rem] hover:bg-black hover:text-white">
                        Open Radar
                    </Link>
                </div>
            </main>
        </div>
    );
}
